'use client'

import { X } from 'lucide-react'
import * as React from 'react'

import 'katex/dist/katex.min.css'

import { sanitizeWithDOMPurify } from '@/lib/dompurify'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface MathDialogProps {
  isOpen: boolean
  onClose: () => void
  onInsert: (latex: string) => void
  onUpdate?: (latex: string) => void
  initialValue?: string
  isEditMode?: boolean
}

const mathExamples = [
  { label: 'Quadratic Formula', latex: 'x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}' },
  { label: 'Pythagorean Theorem', latex: 'a^2 + b^2 = c^2' },
  { label: "Euler's Identity", latex: 'e^{i\\pi} + 1 = 0' },
  { label: 'Summation', latex: '\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}' },
  { label: 'Integral', latex: '\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}' },
  { label: 'Matrix', latex: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
  {
    label: 'Cauchy-Schwarz',
    latex:
      '\\left( \\sum_{k=1}^n a_k b_k \\right)^2 \\leq\n\\left( \\sum_{k=1}^n a_k^2 \\right) \\left( \\sum_{k=1}^n b_k^2 \\right)',
  },
  { label: 'Multiple Equations', latex: 'x + y = z\n\\frac{a}{b} = c\nd^2 = e^2 + f^2' },
  { label: 'Fraction', latex: '\\frac{a}{b}' },
  { label: 'Square Root', latex: '\\sqrt{x}' },
  { label: 'Power', latex: 'x^{n}' },
  { label: 'Subscript', latex: 'x_{i}' },
]

export default function MathDialog({
  isOpen,
  onClose,
  onInsert,
  onUpdate,
  initialValue = '',
  isEditMode = false,
}: MathDialogProps) {
  const [latex, setLatex] = React.useState(initialValue)
  const [preview, setPreview] = React.useState('')
  const sanitizedPreview = React.useMemo(
    () =>
      sanitizeWithDOMPurify(preview, {
        USE_PROFILES: { html: true, svg: true, mathMl: true },
      }),
    [preview]
  )

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  React.useEffect(() => {
    if (isOpen) {
      setLatex(initialValue)
      setPreview(initialValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  React.useEffect(() => {
    if (isOpen && initialValue && initialValue !== latex) {
      setLatex(initialValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, isOpen])

  React.useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (latex) {
      ;(async () => {
        try {
          const katex = (await import('katex')).default

          const preprocessLatex = (latex: string) => {
            // Fix exponents without braces: x^111 -> x^{111}
            // This regex matches ^ followed by one or more digits that are not already in braces
            let processed = latex.replace(/\^(\d+)(?![^{])/g, '^{$1}')
            // Fix subscripts without braces: x_111 -> x_{111}
            processed = processed.replace(/_(\d+)(?![^{])/g, '_{$1}')
            // Fix mixed exponents and subscripts: x^111_222 -> x^{111}_{222}
            processed = processed.replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
            processed = processed.replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')

            // Fix cases like e^{-x^7777} -> e^{-x^{7777}}
            processed = processed.replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')

            return processed
          }

          const formulas = latex.split('\n').filter(formula => formula.trim())

          if (formulas.length === 1) {
            const processedLatex = preprocessLatex(latex.trim())
            const rendered = katex.renderToString(processedLatex, {
              throwOnError: false,
              displayMode: false,
            })
            setPreview(rendered)
          } else {
            // Multiple formulas - render each on a new line
            const renderedFormulas = formulas.map(formula => {
              try {
                const processedLatex = preprocessLatex(formula.trim())
                return katex.renderToString(processedLatex, {
                  throwOnError: false,
                  displayMode: true,
                })
              } catch (error) {
                return `<div class="math-error">Error: ${formula}</div>`
              }
            })

            setPreview(renderedFormulas.join(''))
          }
        } catch (error) {
          const escapedLatex = latex.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          setPreview(`<pre>${escapedLatex}</pre>`)
          // eslint-disable-next-line no-console
          console.warn('KaTeX rendering failed in preview:', error)
        }
      })()
    } else {
      setPreview('')
    }
  }, [latex])

  const handleInsert = () => {
    if (latex.trim()) {
      if (isEditMode && onUpdate) {
        onUpdate(latex.trim())
      } else {
        onInsert(latex.trim())
      }
      onClose()
    }
  }

  const handleExampleClick = (exampleLatex: string) => {
    setLatex(exampleLatex)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleInsert()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Math' : 'Math Editor'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-hidden overflow-y-scroll">
          {/* Left side - Editor */}
          <div className="flex flex-col space-y-4">
            <div className="px-2 space-y-2">
              <Label htmlFor="latex-input">LaTeX Syntax</Label>
              <Textarea
                ref={textareaRef}
                id="latex-input"
                value={latex}
                onChange={e => setLatex(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter LaTeX syntax here... (e.g., x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a})"
                className="min-h-[200px] font-mono text-sm"
                disabled={false}
                readOnly={false}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">Tip: Use Ctrl+Enter to insert quickly</p>
            </div>

            {/* Examples */}
            <div className="">
              <Label className="text-sm font-medium">Examples</Label>
              <div className="grid grid-cols-1 gap-2 mt-2 max-h-32 overflow-y-auto">
                {mathExamples.map((example, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="justify-start text-left h-auto p-2"
                    onClick={() => handleExampleClick(example.latex)}
                  >
                    <div>
                      <div className="font-medium text-xs">{example.label}</div>
                      <div className="text-xs text-gray-500 font-mono truncate whitespace-pre-wrap">
                        {example.latex}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Right side - Preview */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <Label>Preview</Label>
            </div>

            <Card className="flex-1">
              <CardContent className="p-4 h-full">
                <div className="min-h-[200px] flex items-center justify-center">
                  {preview ? (
                    <div className="math-preview text-lg" dangerouslySetInnerHTML={{ __html: sanitizedPreview }} />
                  ) : (
                    <div className="text-gray-400 text-center">
                      <div className="text-4xl mb-2">∑</div>
                      <div>Math preview will appear here</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={!latex.trim()}>
            {isEditMode ? 'Update Math' : 'Insert Math'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <style jsx global>{`
        .math-error {
          color: #dc3545 !important;
          font-style: italic !important;
          background-color: #f8d7da !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          border: 1px solid #f5c6cb !important;
        }

        #latex-input {
          pointer-events: auto !important;
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
        }
      `}</style>
    </Dialog>
  )
}
